import React from 'react'
import {
    bannerSx,
    buttonSx,
    chipSx,
    classToFComponent,
    FCClassContext,
    Field,
    FieldHelp,
    FieldLabel,
    inputSx,
    LifesHubConfirmDialog,
    ModalClose,
    ModalContent,
    ModalFooter,
    ModalFooterActions,
    ModalHead
} from 'src/utils/views'
import { Logger } from 'wdc-cube'

import { styled } from '@mui/system'

import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import SearchIcon from '@mui/icons-material/Search'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Card from '@mui/material/Paper'
import Popover from '@mui/material/Popover'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import {
    FilterCatalogFormScope,
    FilterCatalogScope,
    FilterCatalogShareScope,
    type FilterCatalogTab
} from '../../tcm_scopes'
import { type FilterCatalogOption } from '../../tcm_service'

const LOG = Logger.get('FilterCatalogView')

const TABS: { id: FilterCatalogTab; label: string }[] = [
    { id: 'all', label: 'Todos' },
    { id: 'mine', label: 'Meus' },
    { id: 'shared', label: 'Compartilhados' },
    { id: 'fav', label: 'Favoritos' }
]

const ContextBanner = styled('div')({
    display: 'flex',
    '& .ctx-body': { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 9 },
    '& .ctx-name': { fontSize: 13.5, fontWeight: 750, lineHeight: 1.3 },
    '& .ctx-meta': { fontSize: 11.5, opacity: 0.82, marginTop: 2 },
    '& .ctx-actions': { display: 'flex', gap: 8, flexWrap: 'wrap' }
})

const LibraryCard = styled('div')({
    width: 'min(444px, calc(100vw - 32px))',
    maxHeight: 490,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
})

const LibraryTop = styled('div')({
    display: 'flex',
    gap: 10,
    padding: 13,
    borderBottom: '1px solid var(--grid-line)'
})

const LibraryTabs = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    height: 52,
    flex: '0 0 auto',
    padding: '0 13px',
    overflowX: 'auto',
    overflowY: 'hidden',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': { display: 'none', width: 0, height: 0 }
})

const LibraryTabButton = styled('button', {
    shouldForwardProp: (prop) => prop !== 'selected'
})<{ selected?: boolean }>(({ selected }) => ({
    flex: '0 0 auto',
    height: 32,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    border: 0,
    borderRadius: 999,
    padding: '0 12px',
    fontSize: 12.5,
    fontWeight: 650,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    color: selected ? '#fff' : 'var(--muted)',
    backgroundColor: selected ? 'var(--ink)' : 'transparent',
    '&:hover': {
        backgroundColor: selected ? 'var(--ink)' : 'var(--grid-line)',
        color: selected ? '#fff' : 'var(--ink)'
    },
    '& .n': { opacity: 0.62, fontWeight: 600 }
}))

const ResultsCount = styled('div')({
    padding: '9px 13px 4px',
    fontSize: 12,
    color: 'var(--muted)',
    '& span': { color: 'var(--green-800)', fontWeight: 700, marginRight: 4 }
})

const LibraryList = styled('div')({
    overflowY: 'auto',
    padding: '6px 13px 13px',
    flex: '1 1 auto',
    minHeight: 0,
    display: 'grid',
    gap: 10,
    alignContent: 'start'
})

const GroupHeader = styled('button', {
    shouldForwardProp: (prop) => prop !== 'opened'
})<{ opened?: boolean }>(({ opened }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    width: '100%',
    padding: '9px 2px 1px',
    border: 0,
    background: 'none',
    font: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
    '&:hover .nm, &:hover .chev': { color: 'var(--ink)' },
    '& .badge': {
        width: 23,
        height: 23,
        borderRadius: 7,
        display: 'grid',
        placeItems: 'center',
        flex: '0 0 auto',
        backgroundColor: '#fff',
        border: '1px solid var(--border)',
        color: 'var(--muted)'
    },
    '& .badge svg': { fontSize: 14 },
    '& .nm': {
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: '.09em',
        textTransform: 'uppercase',
        color: 'var(--muted)'
    },
    '& .ct': { marginLeft: 'auto', fontSize: 11, color: 'var(--muted-2)' },
    '& .chev': {
        fontSize: 18,
        color: 'var(--muted)',
        transition: 'transform .16s ease',
        transform: opened ? 'rotate(180deg)' : 'none'
    }
}))

const LibraryItem = styled('div', {
    shouldForwardProp: (prop) => prop !== 'current'
})<{ current?: boolean }>(({ current }) => ({
    display: 'grid',
    gridTemplateColumns: '30px 30px minmax(0, 1fr) 34px',
    gap: 9,
    alignItems: 'center',
    cursor: 'pointer',
    padding: '11px 12px',
    backgroundColor: current ? 'var(--green-050)' : '#fff',
    border: `1px solid ${current ? 'var(--green-700)' : 'var(--border)'}`,
    borderRadius: 'var(--radius-panel, 11px)',
    transition: 'border-color .15s ease, box-shadow .15s ease, background .15s ease',
    '&:hover': {
        borderColor: current ? 'var(--green-700)' : '#7ed984',
        backgroundColor: current ? 'var(--green-050)' : '#fbfffb',
        boxShadow: '0 5px 18px rgba(0,0,0,.04)'
    },
    '& .li-name': { fontWeight: 750, fontSize: 13, marginBottom: 2, lineHeight: 1.3 },
    '& .li-sum': { color: 'var(--muted)', fontSize: 11.5 }
}))

const ItemDetail = styled('div')({
    gridColumn: '1 / -1',
    display: 'grid',
    gap: 10,
    paddingTop: 11,
    marginTop: 2,
    borderTop: '1px solid var(--grid-line)',
    '& .key-row': {
        display: 'grid',
        gridTemplateColumns: 'minmax(92px, .7fr) minmax(0, 1fr)',
        gap: 14,
        alignItems: 'start'
    },
    '& .key': { color: 'var(--muted)', fontSize: 12 },
    '& .value': { fontWeight: 650, fontSize: 12.5 },
    '& .detail-actions': { display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 2 }
})

const ExpandButton = styled(IconButton, {
    shouldForwardProp: (prop) => prop !== 'opened'
})<{ opened?: boolean }>(({ opened }) => ({
    width: 30,
    height: 30,
    borderRadius: 8,
    border: `1px solid ${opened ? 'var(--ink)' : 'var(--border)'}`,
    backgroundColor: opened ? 'var(--ink)' : '#fff',
    color: opened ? '#fff' : '#2d332f',
    '&:hover': { backgroundColor: opened ? 'var(--ink)' : '#edf1ee' },
    '& svg': { fontSize: 18, transition: 'transform .16s ease', transform: opened ? 'rotate(180deg)' : 'none' }
}))

const StarButton = styled(IconButton, {
    shouldForwardProp: (prop) => prop !== 'marked'
})<{ marked?: boolean }>(({ marked }) => ({
    width: 30,
    height: 30,
    borderRadius: 8,
    color: marked ? '#e0a11a' : 'var(--muted-2)',
    '&:hover': { backgroundColor: '#edf1ee', color: marked ? '#e0a11a' : 'var(--warning)' },
    '& svg': { fontSize: 19 }
}))

const KebabButton = styled(IconButton)({
    width: 34,
    height: 34,
    borderRadius: 8,
    color: '#2d332f',
    '&:hover': { backgroundColor: '#edf1ee' }
})

const EmptyBox = styled('div')({
    padding: '48px 20px',
    textAlign: 'center',
    display: 'grid',
    gap: 8,
    justifyItems: 'center',
    '& .ball': {
        width: 46,
        height: 46,
        borderRadius: '50%',
        backgroundColor: 'var(--green-100)',
        display: 'grid',
        placeItems: 'center',
        color: 'var(--green-800)'
    },
    '& h3': { margin: '6px 0 0', fontSize: 16 },
    '& p': { margin: 0, color: 'var(--muted)', fontSize: 13, maxWidth: '34ch' }
})

const Chip = styled('span')({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    minHeight: 25,
    padding: '4px 9px',
    borderRadius: 999,
    fontSize: 11.5,
    fontWeight: 700,
    whiteSpace: 'nowrap'
})

const ChipRow = styled('div')({ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' })

const FormCard = styled(Card)({
    width: 'min(430px, calc(100vw - 28px))',
    maxHeight: 'calc(100vh - 32px)',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 'var(--radius)',
    boxShadow: 'var(--shadow-2)',
    backgroundImage: 'none',
    overflow: 'auto'
})

const CompactModalContent = styled(ModalContent)({ padding: '18px 20px 16px' })
const CompactModalFooter = styled(ModalFooter)({ padding: '13px 20px', justifyContent: 'flex-end' })

const Banner = styled('div')({
    display: 'flex',
    alignItems: 'flex-start',
    gap: 11,
    padding: '12px 14px',
    borderRadius: 10,
    border: '1px solid',
    fontSize: 12,
    '& svg': { fontSize: 17, flex: '0 0 auto', marginTop: 1 }
})

const ChoiceRow = styled('div')({ display: 'flex', gap: 8, flexWrap: 'wrap' })

const ChoiceButton = styled('button', {
    shouldForwardProp: (prop) => prop !== 'selected'
})<{ selected?: boolean }>(({ selected }) => ({
    flex: '1 1 30%',
    minWidth: 100,
    border: `1px solid ${selected ? 'var(--green-700)' : 'var(--border-strong)'}`,
    backgroundColor: selected ? 'var(--green-050)' : '#fff',
    color: selected ? 'var(--green-800)' : 'var(--muted)',
    boxShadow: selected ? '0 0 0 2px rgba(38,229,31,.12)' : 'none',
    borderRadius: 9,
    padding: '11px 8px',
    fontSize: 12,
    fontWeight: 650,
    cursor: 'pointer',
    display: 'grid',
    justifyItems: 'center',
    gap: 6,
    transition: 'border-color .15s ease, background .15s ease, box-shadow .15s ease',
    '& svg': { fontSize: 20 }
}))

export type FilterCatalogViewProps = {
    className?: string
    scope: FilterCatalogScope
}

class FilterCatalogViewClass implements FCClassContext<FilterCatalogViewProps> {
    scope!: FilterCatalogScope

    private _anchorEl: HTMLElement | null = null
    private _menuAnchorEl: HTMLElement | null = null
    private _menuOption?: FilterCatalogOption

    onSyncState({ scope }: FilterCatalogViewProps) {
        this.scope = scope
    }

    render({ className, scope }: FilterCatalogViewProps) {
        const opened = !!scope.openedId
        const tone = !opened ? 'blue' : scope.dirty ? 'warning' : 'green'

        return (
            <div className={className}>
                <ContextBanner sx={bannerSx(tone)}>
                    {this.renderContextIcon(opened, scope.dirty)}
                    <div className="ctx-body">
                        <div>
                            <div className="ctx-name">
                                {opened ? scope.openedTitle : 'Nenhum filtro salvo aberto'}
                                {opened && scope.dirty && (
                                    <Chip sx={{ ...chipSx('orange'), marginLeft: '6px' }}>alterações não salvas</Chip>
                                )}
                                {opened && !scope.dirty && !scope.openedOwner && (
                                    <Chip sx={{ ...chipSx('gray'), marginLeft: '6px' }}>somente leitura</Chip>
                                )}
                            </div>
                            <div className="ctx-meta">
                                {opened
                                    ? `${scope.openedGroup} · ${scope.openedSummary}`
                                    : 'Monte os filtros abaixo ou abra um da biblioteca'}
                            </div>
                        </div>
                        <div className="ctx-actions">{this.renderContextActions(scope, opened)}</div>
                    </div>
                </ContextBanner>

                <Popover
                    open={scope.libraryOpen}
                    anchorEl={this._anchorEl}
                    onClose={this.emitCloseLibrary}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    slotProps={{
                        paper: {
                            sx: {
                                marginTop: '8px',
                                borderRadius: 'var(--radius)',
                                border: '1px solid var(--border)',
                                boxShadow: 'var(--shadow-2)',
                                backgroundImage: 'none'
                            }
                        }
                    }}
                >
                    {this.renderLibrary(scope)}
                </Popover>

                {this.renderRowMenu(scope)}

                <LifesHubConfirmDialog
                    open={!!scope.askYesOrNo}
                    title="Excluir filtro"
                    tone="danger"
                    message={scope.askYesOrNo}
                    confirmLabel="Excluir"
                    cancelLabel="Cancelar"
                    onConfirm={this.emitOnYesResponse}
                    onCancel={this.emitOnNoResponse}
                />
            </div>
        )
    }

    renderContextIcon(opened: boolean, dirty: boolean) {
        if (!opened) {
            return <InfoOutlinedIcon sx={{ fontSize: 18, marginTop: '2px' }} />
        }
        if (dirty) {
            return <WarningAmberIcon sx={{ fontSize: 18, marginTop: '2px' }} />
        }
        return this.scope.openedOwner ? (
            <FolderOutlinedIcon sx={{ fontSize: 18, marginTop: '2px' }} />
        ) : (
            <LockOutlinedIcon sx={{ fontSize: 18, marginTop: '2px' }} />
        )
    }

    renderContextActions(scope: FilterCatalogScope, opened: boolean) {
        if (!opened) {
            return (
                <Button
                    ref={this.bindAnchor}
                    sx={{ ...buttonSx('default', true), width: '100%' }}
                    startIcon={<FolderOutlinedIcon />}
                    aria-expanded={scope.libraryOpen}
                    onClick={this.emitToggleLibrary}
                >
                    Filtros salvos
                </Button>
            )
        }

        if (scope.dirty) {
            return (
                <>
                    {scope.openedOwner && (
                        <Button
                            sx={buttonSx('primary', true)}
                            startIcon={<SaveOutlinedIcon />}
                            onClick={this.emitSaveOpened}
                        >
                            Salvar
                        </Button>
                    )}
                    <Button
                        sx={buttonSx(scope.openedOwner ? 'default' : 'primary', true)}
                        startIcon={scope.openedOwner ? undefined : <ContentCopyIcon />}
                        onClick={this.emitSaveAsNew}
                    >
                        {scope.openedOwner ? 'Salvar como novo' : 'Salvar como meu'}
                    </Button>
                    <Button sx={buttonSx('tertiary', true)} onClick={this.emitDiscardChanges}>
                        Descartar
                    </Button>
                </>
            )
        }

        return (
            <>
                <Button
                    ref={this.bindAnchor}
                    sx={buttonSx('default', true)}
                    aria-expanded={scope.libraryOpen}
                    onClick={this.emitToggleLibrary}
                >
                    Trocar de filtro
                </Button>
                <Button sx={buttonSx('tertiary', true)} onClick={this.emitCloseOpened}>
                    Fechar filtro
                </Button>
            </>
        )
    }

    renderLibrary(scope: FilterCatalogScope) {
        const options = scope.getVisibleOptions()

        return (
            <LibraryCard>
                <LibraryTop>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        placeholder="Buscar filtro salvo"
                        value={scope.searchText}
                        onChange={this.handleSearchChange}
                        sx={inputSx}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: 18, color: 'var(--muted-2)' }} />
                                    </InputAdornment>
                                )
                            }
                        }}
                    />
                    <Button
                        sx={{ ...buttonSx('brand'), flex: '0 0 auto', minWidth: 'fit-content' }}
                        onClick={this.emitCreate}
                        startIcon={<SaveOutlinedIcon />}
                    >
                        Salvar atual
                    </Button>
                </LibraryTop>

                <LibraryTabs role="tablist">
                    {TABS.map((tab) => (
                        <LibraryTabButton
                            key={tab.id}
                            role="tab"
                            type="button"
                            aria-selected={scope.tab === tab.id}
                            selected={scope.tab === tab.id}
                            onClick={() => this.emitTabChange(tab.id)}
                        >
                            {tab.label} <span className="n">{scope.countByTab(tab.id)}</span>
                        </LibraryTabButton>
                    ))}
                </LibraryTabs>

                <ResultsCount>
                    <span>{options.length}</span>
                    {options.length === 1 ? 'filtro encontrado' : 'filtros encontrados'}
                </ResultsCount>

                <LibraryList>{this.renderItems(scope, options)}</LibraryList>
            </LibraryCard>
        )
    }

    renderItems(scope: FilterCatalogScope, options: FilterCatalogOption[]) {
        if (options.length === 0) {
            return (
                <EmptyBox>
                    <div className="ball">
                        <SearchIcon />
                    </div>
                    <h3>Nenhum filtro encontrado</h3>
                    <p>Ajuste a busca ou salve a consulta que está montada no painel.</p>
                    <Button sx={{ ...buttonSx('brand', true), marginTop: '6px' }} onClick={this.emitCreate}>
                        Salvar atual
                    </Button>
                </EmptyBox>
            )
        }

        const nodes: React.JSX.Element[] = []
        const searching = !!scope.searchText.trim()
        let lastKey = ''
        let groupOpened = false

        options.forEach((option) => {
            const key = `${option.owner ? 'own' : 'shared'}|${option.group}`
            if (key !== lastKey) {
                lastKey = key
                groupOpened = searching || scope.expandedGroups.indexOf(key) !== -1
                const count = options.filter((it) => `${it.owner ? 'own' : 'shared'}|${it.group}` === key).length
                nodes.push(
                    <GroupHeader
                        key={`grp-${key}`}
                        type="button"
                        opened={groupOpened}
                        aria-expanded={groupOpened}
                        onClick={() => this.emitToggleGroup(key)}
                    >
                        <span className="badge">{option.owner ? <FolderOutlinedIcon /> : <GroupOutlinedIcon />}</span>
                        <span className="nm">
                            {option.owner ? 'Pasta · ' : 'Compartilhado · '}
                            {option.group}
                        </span>
                        <span className="ct">{count === 1 ? '1 filtro' : `${count} filtros`}</span>
                        <ExpandMoreIcon className="chev" />
                    </GroupHeader>
                )
            }

            if (groupOpened) {
                nodes.push(this.renderItem(scope, option))
            }
        })

        return nodes
    }

    renderItem(scope: FilterCatalogScope, option: FilterCatalogOption) {
        const expanded = scope.expandedIds.indexOf(option.id) !== -1
        const fields = scope.getFieldLabels(option)
        const fieldCount = fields.length || option.fieldCount
        const summary = `${fieldCount === 1 ? '1 filtro' : `${fieldCount} filtros`} · ${
            option.owner
                ? option.sharedCount > 0
                    ? `compartilhado com ${option.sharedCount}`
                    : 'só você'
                : `por ${option.ownerName ?? 'outro usuário'}`
        }`

        return (
            <LibraryItem
                key={option.id}
                role="button"
                tabIndex={0}
                aria-label={`Aplicar ${option.title}`}
                current={scope.openedId === option.id}
                onClick={() => this.emitApply(option)}
                onKeyDown={(e) => this.handleItemKeyDown(e, option)}
            >
                <Tooltip title={expanded ? 'Recolher detalhe' : 'Ver detalhe'}>
                    <ExpandButton
                        opened={expanded}
                        aria-expanded={expanded}
                        aria-label={`${expanded ? 'Recolher' : 'Ver'} detalhe de ${option.title}`}
                        onClick={(e) => this.emitToggleExpanded(e, option)}
                    >
                        <ExpandMoreIcon />
                    </ExpandButton>
                </Tooltip>

                <Tooltip title={option.favorite ? 'Remover dos favoritos' : 'Marcar como favorito'}>
                    <StarButton
                        marked={option.favorite}
                        aria-pressed={option.favorite}
                        aria-label={option.favorite ? 'Remover dos favoritos' : 'Marcar como favorito'}
                        onClick={(e) => this.emitToggleFavorite(e, option)}
                    >
                        {option.favorite ? <StarIcon /> : <StarBorderIcon />}
                    </StarButton>
                </Tooltip>

                <div style={{ minWidth: 0 }}>
                    <div className="li-name">{option.title}</div>
                    <div className="li-sum">{summary}</div>
                </div>

                <KebabButton aria-label={`Ações de ${option.title}`} onClick={(e) => this.emitOpenRowMenu(e, option)}>
                    <MoreVertIcon />
                </KebabButton>

                {expanded && (
                    <ItemDetail onClick={(e) => e.stopPropagation()}>
                        <div className="key-row">
                            <span className="key">Campos</span>
                            <ChipRow className="value">
                                {fields.length > 0 ? (
                                    fields.map((label) => (
                                        <Chip key={label} sx={chipSx('gray')}>
                                            {label}
                                        </Chip>
                                    ))
                                ) : (
                                    <Typography variant="body2" sx={{ color: 'var(--muted)', fontSize: 12.5 }}>
                                        Sem campos registrados
                                    </Typography>
                                )}
                            </ChipRow>
                        </div>
                        <div className="key-row">
                            <span className="key">{option.owner ? 'Compartilhado com' : 'Origem'}</span>
                            <span className="value">
                                {option.owner
                                    ? option.sharedCount > 0
                                        ? `${option.sharedCount} ${
                                              option.sharedCount > 1 ? 'destinatários' : 'destinatário'
                                          }`
                                        : 'Ninguém além de você'
                                    : (option.ownerName ?? 'Compartilhado com você')}
                            </span>
                        </div>
                        <div className="detail-actions">
                            <Button
                                sx={buttonSx('primary', true)}
                                startIcon={<CheckIcon />}
                                onClick={() => this.emitApply(option)}
                            >
                                Aplicar filtro
                            </Button>
                            {option.owner && (
                                <Button
                                    sx={buttonSx('default', true)}
                                    startIcon={<GroupOutlinedIcon />}
                                    onClick={() => this.emitShare(option)}
                                >
                                    Compartilhar
                                </Button>
                            )}
                            <Button
                                sx={buttonSx('tertiary', true)}
                                startIcon={<ContentCopyIcon />}
                                onClick={() => this.emitDuplicate(option)}
                            >
                                Duplicar
                            </Button>
                        </div>
                    </ItemDetail>
                )}
            </LibraryItem>
        )
    }

    renderRowMenu(scope: FilterCatalogScope) {
        const option = this._menuOption
        const owner = !!option?.owner

        return (
            <Menu
                open={!!this._menuAnchorEl}
                anchorEl={this._menuAnchorEl}
                onClose={this.emitCloseRowMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: {
                        sx: {
                            minWidth: 198,
                            padding: '7px',
                            borderRadius: '10px',
                            border: '1px solid var(--border)',
                            boxShadow: 'var(--shadow-2)',
                            backgroundImage: 'none',
                            '& .MuiMenuItem-root': {
                                gap: '9px',
                                padding: '9px 10px',
                                borderRadius: '7px',
                                fontSize: 13,
                                fontWeight: 600
                            },
                            '& .MuiMenuItem-root svg': { fontSize: 18 }
                        }
                    }
                }}
            >
                <MenuItem onClick={() => this.emitMenuAction('apply')}>
                    <CheckIcon /> Aplicar filtro
                </MenuItem>
                <MenuItem onClick={() => this.emitMenuAction('expand')}>
                    <ExpandMoreIcon /> {option && scope.expandedIds.indexOf(option.id) !== -1 ? 'Recolher' : 'Ver'}{' '}
                    detalhe
                </MenuItem>
                <MenuItem onClick={() => this.emitMenuAction('duplicate')}>
                    <ContentCopyIcon /> Duplicar
                </MenuItem>
                {owner && (
                    <MenuItem onClick={() => this.emitMenuAction('rename')}>
                        <DriveFileRenameOutlineIcon /> Renomear
                    </MenuItem>
                )}
                {owner && (
                    <MenuItem onClick={() => this.emitMenuAction('share')}>
                        <GroupOutlinedIcon /> Compartilhar
                    </MenuItem>
                )}
                {owner && (
                    <MenuItem sx={{ color: 'var(--danger)' }} onClick={() => this.emitMenuAction('delete')}>
                        <DeleteOutlineIcon /> Excluir
                    </MenuItem>
                )}
            </Menu>
        )
    }

    readonly bindAnchor = (el: HTMLElement | null) => {
        if (el) {
            this._anchorEl = el
        }
    }

    readonly emitToggleLibrary = () => {
        this.scope.onToggleLibrary(!this.scope.libraryOpen).catch(LOG.caught)
    }

    readonly emitCloseLibrary = () => {
        this.scope.onToggleLibrary(false).catch(LOG.caught)
    }

    readonly emitTabChange = (tab: FilterCatalogTab) => {
        this.scope.onTabChange(tab).catch(LOG.caught)
    }

    readonly handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.scope.onSearchChange(e.target.value).catch(LOG.caught)
    }

    readonly handleItemKeyDown = (e: React.KeyboardEvent<HTMLElement>, option: FilterCatalogOption) => {
        if (e.target !== e.currentTarget) {
            return
        }
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            this.emitApply(option)
        }
    }

    readonly emitToggleExpanded = (e: React.MouseEvent<HTMLElement>, option: FilterCatalogOption) => {
        e.preventDefault()
        e.stopPropagation()
        this.scope.onToggleExpanded(option.id).catch(LOG.caught)
    }

    readonly emitToggleGroup = (key: string) => {
        if (this.scope.searchText.trim()) {
            return
        }
        this.scope.onToggleGroup(key).catch(LOG.caught)
    }

    readonly emitToggleFavorite = (e: React.MouseEvent<HTMLElement>, option: FilterCatalogOption) => {
        e.preventDefault()
        e.stopPropagation()
        this.scope.onToggleFavorite(option).catch(LOG.caught)
    }

    readonly emitApply = (option: FilterCatalogOption) => {
        this.scope.onApplyFilter(option.id).catch(LOG.caught)
    }

    readonly emitCreate = () => {
        this.scope.onCreate().catch(LOG.caught)
    }

    readonly emitSaveOpened = () => {
        this.scope.onSaveOpened().catch(LOG.caught)
    }

    readonly emitSaveAsNew = () => {
        this.scope.onSaveAsNew().catch(LOG.caught)
    }

    readonly emitDiscardChanges = () => {
        this.scope.onDiscardChanges().catch(LOG.caught)
    }

    readonly emitCloseOpened = () => {
        this.scope.onCloseOpened().catch(LOG.caught)
    }

    readonly emitShare = (option: FilterCatalogOption) => {
        this.scope.onShare(option).catch(LOG.caught)
    }

    readonly emitDuplicate = (option: FilterCatalogOption) => {
        this.scope.onDuplicate(option).catch(LOG.caught)
    }

    readonly emitOpenRowMenu = (e: React.MouseEvent<HTMLElement>, option: FilterCatalogOption) => {
        e.preventDefault()
        e.stopPropagation()
        this._menuAnchorEl = e.currentTarget
        this._menuOption = option
        this.scope.update()
    }

    readonly emitCloseRowMenu = () => {
        this._menuAnchorEl = null
        this._menuOption = undefined
        this.scope.update()
    }

    readonly emitMenuAction = (action: 'apply' | 'expand' | 'duplicate' | 'rename' | 'share' | 'delete') => {
        const option = this._menuOption
        this.emitCloseRowMenu()

        if (!option) {
            return
        }

        switch (action) {
            case 'apply':
                this.scope.onApplyFilter(option.id).catch(LOG.caught)
                break
            case 'expand':
                this.scope.onToggleExpanded(option.id).catch(LOG.caught)
                break
            case 'duplicate':
                this.scope.onDuplicate(option).catch(LOG.caught)
                break
            case 'rename':
                this.scope.onRename(option).catch(LOG.caught)
                break
            case 'share':
                this.scope.onShare(option).catch(LOG.caught)
                break
            case 'delete':
                this.scope.onDelete(option).catch(LOG.caught)
                break
        }
    }

    readonly emitOnYesResponse = () => {
        this.scope.onYesNoResponse(true).catch(LOG.caught)
    }

    readonly emitOnNoResponse = () => {
        this.scope.onYesNoResponse(false).catch(LOG.caught)
    }
}

export const FilterCatalogView = classToFComponent(FilterCatalogViewClass, React)
export default FilterCatalogView

const FORM_TITLES: Record<string, { title: string; subtitle: string }> = {
    create: {
        title: 'Salvar filtro',
        subtitle: 'A consulta montada no painel vira um filtro reutilizável.'
    },
    duplicate: {
        title: 'Duplicar filtro',
        subtitle: 'A cópia nasce na sua pasta, sem destinatários.'
    },
    rename: {
        title: 'Renomear filtro',
        subtitle: 'Muda o nome e a pasta. A consulta salva e os destinatários continuam como estão.'
    },
    'save-as-new': {
        title: 'Salvar como novo',
        subtitle: 'Guarda as alterações em um filtro separado, sem mexer no original.'
    }
}

export type FilterCatalogFormViewProps = {
    className?: string
    scope: FilterCatalogFormScope
}

class FilterCatalogFormViewClass implements FCClassContext<FilterCatalogFormViewProps> {
    scope!: FilterCatalogFormScope

    onSyncState({ scope }: FilterCatalogFormViewProps) {
        this.scope = scope
    }

    render({ className }: FilterCatalogFormViewProps) {
        const { scope } = this
        const texts = FORM_TITLES[scope.mode] ?? FORM_TITLES.create
        const showDepartments = scope.shareMode === 'dept' || scope.shareMode === 'pick'
        const showUsers = scope.shareMode === 'pick'

        return (
            <FormCard className={className} elevation={3}>
                <CompactModalContent>
                    <ModalHead>
                        <div>
                            <h2>{texts.title}</h2>
                            <p>{texts.subtitle}</p>
                        </div>
                        <ModalClose aria-label="Fechar" onClick={this.emitOnClose}>
                            <CloseIcon />
                        </ModalClose>
                    </ModalHead>

                    <Field>
                        <FieldLabel htmlFor="tcmCatalogTitle">Nome do filtro</FieldLabel>
                        <TextField
                            id="tcmCatalogTitle"
                            size="small"
                            sx={inputSx}
                            value={scope.title}
                            onChange={this.handleTitleChange}
                        />
                        {!!scope.duplicateOf && (
                            <Banner sx={{ ...bannerSx('warning'), marginTop: '2px' }}>
                                <WarningAmberIcon />
                                <span>
                                    Já existe <b>“{scope.title.trim()}”</b> na pasta {scope.group.trim()} — salvar
                                    substitui a versão anterior para todos que a recebem.
                                </span>
                            </Banner>
                        )}
                    </Field>

                    <Field>
                        <FieldLabel htmlFor="tcmCatalogGroup">Pasta</FieldLabel>
                        <Autocomplete
                            id="tcmCatalogGroup"
                            freeSolo
                            disableClearable
                            options={scope.groupOptions}
                            value={scope.group}
                            onInputChange={this.handleGroupChange}
                            renderInput={(params) => <TextField {...params} size="small" sx={inputSx} />}
                        />
                        <FieldHelp>Organiza a sua lista. Não tem relação com departamentos.</FieldHelp>
                    </Field>

                    {scope.mode !== 'rename' && (
                        <Field>
                            <FieldLabel>Quem pode ver</FieldLabel>
                            <ChoiceRow>
                                <ChoiceButton
                                    type="button"
                                    selected={scope.shareMode === 'me'}
                                    onClick={() => this.emitShareMode('me')}
                                >
                                    <PersonOutlineIcon />
                                    Só eu
                                </ChoiceButton>
                                <ChoiceButton
                                    type="button"
                                    selected={scope.shareMode === 'dept'}
                                    onClick={() => this.emitShareMode('dept')}
                                >
                                    <BusinessOutlinedIcon />
                                    Meu departamento
                                </ChoiceButton>
                                <ChoiceButton
                                    type="button"
                                    selected={scope.shareMode === 'pick'}
                                    onClick={() => this.emitShareMode('pick')}
                                >
                                    <GroupOutlinedIcon />
                                    Escolher…
                                </ChoiceButton>
                            </ChoiceRow>
                            <FieldHelp>
                                Quem recebe pode aplicar e duplicar o filtro. Editar por cima, só você.
                            </FieldHelp>
                        </Field>
                    )}

                    {scope.mode !== 'rename' && showDepartments && (
                        <Field>
                            <FieldLabel htmlFor="tcmCatalogGroups">Departamentos</FieldLabel>
                            <Autocomplete
                                id="tcmCatalogGroups"
                                multiple
                                disableCloseOnSelect
                                options={(scope.allGroups ?? []).map((g) => g.id).filter((id): id is number => !!id)}
                                value={scope.groups}
                                getOptionLabel={(id) => scope.allGroups?.find((g) => g.id === id)?.name ?? String(id)}
                                onChange={this.handleGroupsChange}
                                renderInput={(params) => <TextField {...params} size="small" sx={inputSx} />}
                            />
                        </Field>
                    )}

                    {scope.mode !== 'rename' && showUsers && (
                        <Field>
                            <FieldLabel htmlFor="tcmCatalogUsers">Pessoas</FieldLabel>
                            <Autocomplete
                                id="tcmCatalogUsers"
                                multiple
                                disableCloseOnSelect
                                options={(scope.allUsers ?? []).map((u) => u.id).filter((id): id is number => !!id)}
                                value={scope.users}
                                getOptionLabel={(id) => {
                                    const user = scope.allUsers?.find((u) => u.id === id)
                                    return user?.full_name || user?.name || String(id)
                                }}
                                onChange={this.handleUsersChange}
                                renderInput={(params) => <TextField {...params} size="small" sx={inputSx} />}
                            />
                        </Field>
                    )}
                </CompactModalContent>

                <CompactModalFooter>
                    <ModalFooterActions>
                        <Button sx={buttonSx()} onClick={this.emitOnClose}>
                            Cancelar
                        </Button>
                        <Button sx={buttonSx('primary')} startIcon={<SaveOutlinedIcon />} onClick={this.emitOnSave}>
                            Salvar
                        </Button>
                    </ModalFooterActions>
                </CompactModalFooter>
            </FormCard>
        )
    }

    readonly emitOnClose = () => {
        this.scope.onClose().catch(LOG.caught)
    }

    readonly emitOnSave = () => {
        this.scope.onSave().catch(LOG.caught)
    }

    readonly emitShareMode = (mode: string) => {
        this.scope.onShareModeChange(mode).catch(LOG.caught)
    }

    readonly handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.scope.onTitleChange(e.target.value).catch(LOG.caught)
    }

    readonly handleGroupChange = (_event: React.SyntheticEvent, value: string) => {
        this.scope.onGroupChange(value).catch(LOG.caught)
    }

    readonly handleGroupsChange = (_event: React.SyntheticEvent, value: number[]) => {
        this.scope.onGroupsChange(value).catch(LOG.caught)
    }

    readonly handleUsersChange = (_event: React.SyntheticEvent, value: number[]) => {
        this.scope.onUsersChange(value).catch(LOG.caught)
    }
}

export const FilterCatalogFormView = classToFComponent(FilterCatalogFormViewClass, React)

const Recipient = styled('li')({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '9px 11px',
    border: '1px solid var(--border)',
    borderRadius: 9,
    backgroundColor: 'var(--surface-2)',
    '& .who': { flex: 1, fontWeight: 650, fontSize: 13, minWidth: 0 }
})

const RecipientList = styled('ul')({ display: 'grid', gap: 7, margin: 0, padding: 0, listStyle: 'none' })

export type FilterCatalogShareViewProps = {
    className?: string
    scope: FilterCatalogShareScope
}

class FilterCatalogShareViewClass implements FCClassContext<FilterCatalogShareViewProps> {
    scope!: FilterCatalogShareScope

    onSyncState({ scope }: FilterCatalogShareViewProps) {
        this.scope = scope
    }

    render({ className }: FilterCatalogShareViewProps) {
        const { scope } = this
        const total = scope.groups.length + scope.users.length

        return (
            <FormCard className={className} elevation={3}>
                <CompactModalContent>
                    <ModalHead>
                        <div>
                            <h2>Compartilhar filtro</h2>
                            <p>{scope.title}</p>
                        </div>
                        <ModalClose aria-label="Fechar" onClick={this.emitOnClose}>
                            <CloseIcon />
                        </ModalClose>
                    </ModalHead>

                    <Field>
                        <FieldLabel htmlFor="tcmShareGroups">Departamentos</FieldLabel>
                        <Autocomplete
                            id="tcmShareGroups"
                            multiple
                            disableCloseOnSelect
                            options={(scope.allGroups ?? []).map((g) => g.id).filter((id): id is number => !!id)}
                            value={scope.groups}
                            getOptionLabel={(id) => scope.groupName(id)}
                            onChange={this.handleGroupsChange}
                            renderInput={(params) => (
                                <TextField {...params} size="small" sx={inputSx} placeholder="Buscar departamento" />
                            )}
                        />
                    </Field>

                    <Field>
                        <FieldLabel htmlFor="tcmShareUsers">Pessoas</FieldLabel>
                        <Autocomplete
                            id="tcmShareUsers"
                            multiple
                            disableCloseOnSelect
                            options={(scope.allUsers ?? []).map((u) => u.id).filter((id): id is number => !!id)}
                            value={scope.users}
                            getOptionLabel={(id) => scope.userName(id)}
                            onChange={this.handleUsersChange}
                            renderInput={(params) => (
                                <TextField {...params} size="small" sx={inputSx} placeholder="Buscar pessoa" />
                            )}
                        />
                    </Field>

                    <Field>
                        <FieldLabel>
                            Com acesso hoje <Chip sx={{ ...chipSx('gray'), marginLeft: '4px' }}>{total}</Chip>
                        </FieldLabel>
                        <RecipientList>
                            {total === 0 && (
                                <Recipient>
                                    <span className="who" style={{ fontWeight: 500, color: 'var(--muted)' }}>
                                        Ninguém além de você.
                                    </span>
                                </Recipient>
                            )}
                            {scope.groups.map((id) => (
                                <Recipient key={`g-${id}`}>
                                    <BusinessOutlinedIcon sx={{ fontSize: 18, color: '#285797' }} />
                                    <span className="who">{scope.groupName(id)}</span>
                                    <Chip sx={chipSx('blue')}>departamento</Chip>
                                    <IconButton
                                        size="small"
                                        aria-label={`Remover ${scope.groupName(id)}`}
                                        onClick={() => this.emitRemoveGroup(id)}
                                    >
                                        <CloseIcon sx={{ fontSize: 15 }} />
                                    </IconButton>
                                </Recipient>
                            ))}
                            {scope.users.map((id) => (
                                <Recipient key={`u-${id}`}>
                                    <PersonOutlineIcon sx={{ fontSize: 18, color: 'var(--purple)' }} />
                                    <span className="who">{scope.userName(id)}</span>
                                    <Chip sx={chipSx('purple')}>pessoa</Chip>
                                    <IconButton
                                        size="small"
                                        aria-label={`Remover ${scope.userName(id)}`}
                                        onClick={() => this.emitRemoveUser(id)}
                                    >
                                        <CloseIcon sx={{ fontSize: 15 }} />
                                    </IconButton>
                                </Recipient>
                            ))}
                        </RecipientList>
                    </Field>

                    <Banner sx={bannerSx('blue')}>
                        <InfoOutlinedIcon />
                        <span>
                            Quem recebe aplica e duplica o filtro. Ao salvar uma alteração, todos passam a ver a versão
                            nova.
                        </span>
                    </Banner>
                </CompactModalContent>

                <CompactModalFooter>
                    <ModalFooterActions>
                        <Button sx={buttonSx()} onClick={this.emitOnClose}>
                            Cancelar
                        </Button>
                        <Button sx={buttonSx('primary')} startIcon={<SaveOutlinedIcon />} onClick={this.emitOnSave}>
                            Salvar
                        </Button>
                    </ModalFooterActions>
                </CompactModalFooter>
            </FormCard>
        )
    }

    readonly emitOnClose = () => {
        this.scope.onClose().catch(LOG.caught)
    }

    readonly emitOnSave = () => {
        this.scope.onSave().catch(LOG.caught)
    }

    readonly handleGroupsChange = (_event: React.SyntheticEvent, value: number[]) => {
        this.scope.onGroupsChange(value).catch(LOG.caught)
    }

    readonly handleUsersChange = (_event: React.SyntheticEvent, value: number[]) => {
        this.scope.onUsersChange(value).catch(LOG.caught)
    }

    readonly emitRemoveGroup = (id: number) => {
        this.scope.onGroupsChange(this.scope.groups.filter((it) => it !== id)).catch(LOG.caught)
    }

    readonly emitRemoveUser = (id: number) => {
        this.scope.onUsersChange(this.scope.users.filter((it) => it !== id)).catch(LOG.caught)
    }
}

export const FilterCatalogShareView = classToFComponent(FilterCatalogShareViewClass, React)
